import { Link, createFileRoute } from '@tanstack/react-router'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Api, getLogo, getLspName } from '@/lib/utils';
import { Order, TrackHistoryResponse } from '@/lib/interfaces';
import { useStore } from '@/store';
import { useShallow } from 'zustand/shallow';
import { useEffect, useRef, useState } from 'react';
import EventTimeline from '@/components/features/event-timeline';
import { Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import markerIcon from '@/assets/loc_marker.png'
import storeIcon from '@/assets/location_pickup.png'
import homeIcon from '@/assets/location_drop.png'
import { Logs, Phone, RefreshCcw, X } from 'lucide-react';
import Tooltip from '@/components/ui/tooltip';

dayjs.extend(advancedFormat)

export const Route = createFileRoute('/_auth/track/$orderId')({
    component: RouteComponent,
})

function RouteComponent() {
    const { orderId } = Route.useParams()
    const { token, loggedIn } = useStore(useShallow(state => ({
        token: state.token,
        loggedIn: state.loggedIn
    })))

    const [highlightMarkerIndex, setMarkerIndex] = useState(-1)
    const [showEventTimeline, setShowEventTimeline] = useState(true)
    const [liveRiderLocation, setLiveRiderLocation] = useState<{ lat: number, lng: number, updatedAt?: string } | undefined>(undefined)

    const getTrackHistory = useQuery({
        queryKey: ['trackHistory', orderId],
        queryFn: () => Api<TrackHistoryResponse>('/webui/order/track_history', "POST", { "network_order_id": orderId }, { token }),
        staleTime: Infinity,
        refetchInterval: 200 * 1000,
        refetchOnWindowFocus: false,
        enabled: loggedIn
    })

    const getOrderInfo = useQuery({
        queryKey: ['orderInfoForTrack', orderId],
        queryFn: () => Api<{ status: number, order: Order }>('/webui/order/info', "POST", { order: { id: orderId } }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false,
        enabled: loggedIn && !!orderId
    })

    const forceRefreshTrackLocation = useMutation({
        mutationFn: () => {
            const actualOrderId = getOrderInfo.data?.order?.orderId
            if (!actualOrderId) {
                return Promise.reject({ message: 'Order id not available' })
            }
            return Api<{ status: number, order?: { lat?: number, lng?: number, updated_at?: string } }>(
                '/webui/order/track',
                "POST",
                { order: { id: actualOrderId }, force_call: true },
                { token }
            )
        },
        onSuccess: (resp) => {
            if (typeof resp.order?.lat === 'number' && typeof resp.order?.lng === 'number') {
                setLiveRiderLocation({
                    lat: resp.order.lat,
                    lng: resp.order.lng,
                    updatedAt: resp.order.updated_at
                })
            }
            getTrackHistory.refetch()
        }
    })

    const rawLocationHistory = Array.isArray(getTrackHistory.data?.location_history) ? getTrackHistory.data.location_history : []
    const trackHistory = getTrackHistory.data ? { ...getTrackHistory.data, location_history: rawLocationHistory.filter(e => /[0-9.]+/.test(e.lat) && /[0-9.]+/.test(e.lng)) } : undefined
    const orderInfo = getOrderInfo.data?.order
    const riderName = trackHistory?.rider_name || trackHistory?.riderName || orderInfo?.riderName
    const riderPhone = trackHistory?.rider_phone || trackHistory?.riderNumber || orderInfo?.riderNumber
    const lspId = orderInfo?.providerId || orderInfo?.bppId
    const lspLogo = lspId ? getLogo(lspId) : ''
    const lspName = lspId ? getLspName(orderInfo?.bppId, orderInfo?.providerId) : ''

    const maps = useMapsLibrary("maps");

    const mapObj = useMap()

    const polylinesRef = useRef<google.maps.Polyline[]>([])

    useEffect(() => {
        if (!maps || !mapObj || !trackHistory || trackHistory.location_history.length === 0) {
            return
        }

        polylinesRef.current.forEach(polyline => polyline.setMap(null))
        polylinesRef.current = []

        const prePickup = []
        const postPickup = []
        const rtoStart = trackHistory.rto_initiated_at ? dayjs(trackHistory.rto_initiated_at) : null

        for (let i = 0; i < trackHistory.location_history.length; i++) {
            const point = trackHistory.location_history[i]
            if (dayjs(point.updated_at).format('HH:mm:ss') <= dayjs(trackHistory.pickedup_at).format('HH:mm:ss')) {
                prePickup.push({
                    lat: parseFloat(point.lat),
                    lng: parseFloat(point.lng)
                })
            } else if (!rtoStart || dayjs(point.updated_at).isBefore(rtoStart)) {
                if (postPickup.length === 0 && prePickup.length > 0) {
                    postPickup.push({
                        lat: parseFloat(trackHistory.location_history[i - 1].lat),
                        lng: parseFloat(trackHistory.location_history[i - 1].lng)
                    })
                }
                postPickup.push({
                    lat: parseFloat(point.lat),
                    lng: parseFloat(point.lng)
                })
            }
        }

        if (prePickup.length > 0) {
            const prePickupPath = new maps.Polyline({
                path: prePickup,
                geodesic: true,
                strokeColor: "#2563eb",
                strokeOpacity: 1,
                strokeWeight: 3,
            })

            prePickupPath.setMap(mapObj)
            polylinesRef.current.push(prePickupPath)
        }

        if (postPickup.length > 0) {
            const postPickupPath = new maps.Polyline({
                path: postPickup,
                geodesic: true,
                strokeColor: "#00cc00",
                strokeOpacity: 1,
                strokeWeight: 3,
            })

            postPickupPath.setMap(mapObj)
            polylinesRef.current.push(postPickupPath)
        }

        if (rtoStart) {
            const rtoPoints = trackHistory.location_history
                .filter(e => dayjs(e.updated_at).isAfter(rtoStart) || dayjs(e.updated_at).isSame(rtoStart))
                .map(e => ({ lat: parseFloat(e.lat), lng: parseFloat(e.lng) }))
            if (postPickup.length > 0) {
                const lastPostPickup = postPickup[postPickup.length - 1]
                if (rtoPoints.length === 0 || rtoPoints[0].lat !== lastPostPickup.lat || rtoPoints[0].lng !== lastPostPickup.lng) {
                    rtoPoints.unshift(lastPostPickup)
                }
            }
            if (rtoPoints.length > 0) {
                const rtoPath = new maps.Polyline({
                    path: rtoPoints,
                    geodesic: true,
                    strokeColor: "#ff9900",
                    strokeOpacity: 1,
                    strokeWeight: 3,
                })

                rtoPath.setMap(mapObj)
                polylinesRef.current.push(rtoPath)
            }
        }
    }, [maps, mapObj, trackHistory])

    const getEventTimelines = () => {
        let timelines: {
            name: string,
            timestamp: string,
            line3?: string,
            line4?: string,
            type?: 'red' | 'green' | 'orange' | 'blue'
        }[] = []
        if (trackHistory) {
            const getTimelineType = (timestamp: string): 'red' | 'green' | 'orange' | 'blue' => {
                if (trackHistory.rto_initiated_at) {
                    const rtoStart = dayjs(trackHistory.rto_initiated_at)
                    if (dayjs(timestamp).isAfter(rtoStart) || dayjs(timestamp).isSame(rtoStart)) {
                        return 'orange'
                    }
                }

                if (trackHistory.pickedup_at) {
                    const pickupAt = dayjs(trackHistory.pickedup_at)
                    if (dayjs(timestamp).isAfter(pickupAt) || dayjs(timestamp).isSame(pickupAt)) {
                        return 'green'
                    }
                }

                return 'blue'
            }

            if (trackHistory.created_at) {
                timelines.push({
                    name: 'Order Created',
                    timestamp: trackHistory.created_at,
                    type: getTimelineType(trackHistory.created_at)
                })
            }

            if (trackHistory.order_ready_at) {
                timelines.push({
                    name: 'Order Ready',
                    timestamp: trackHistory.order_ready_at,
                    type: getTimelineType(trackHistory.order_ready_at)
                })
            }

            if (trackHistory.assigned_at) {
                timelines.push({
                    name: 'Agent Assigned',
                    timestamp: trackHistory.assigned_at,
                    type: getTimelineType(trackHistory.assigned_at)
                })
            }

            if (trackHistory.at_pickup_at) {
                timelines.push({
                    name: 'Reached Pickup',
                    timestamp: trackHistory.at_pickup_at,
                    type: getTimelineType(trackHistory.at_pickup_at)
                })
            }

            if (trackHistory.pickedup_at) {
                timelines.push({
                    name: 'Order picked up',
                    timestamp: trackHistory.pickedup_at,
                    type: getTimelineType(trackHistory.pickedup_at)
                })
            }

            if (trackHistory.at_delivery_at) {
                timelines.push({
                    name: 'Reached Delivery',
                    timestamp: trackHistory.at_delivery_at,
                    type: getTimelineType(trackHistory.at_delivery_at)
                })
            }

            if (trackHistory.delivered_at) {
                timelines.push({
                    name: 'Order Delivered',
                    timestamp: trackHistory.delivered_at,
                    type: getTimelineType(trackHistory.delivered_at)
                })
            }

            if (trackHistory.cancelled_at && !trackHistory.rto_initiated_at && !trackHistory.rto_delivered_at) {
                timelines.push({
                    name: 'Order Cancelled',
                    timestamp: trackHistory.cancelled_at,
                    type: 'red'
                })
            }

            if (trackHistory.rto_initiated_at) {
                timelines.push({
                    name: 'RTO initiated',
                    timestamp: trackHistory.rto_initiated_at,
                    type: 'orange'
                })
            }

            if (trackHistory.rto_delivered_at) {
                timelines.push({
                    name: 'RTO Delivered',
                    timestamp: trackHistory.rto_delivered_at,
                    type: 'orange'
                })
            }
        }

        return timelines.sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf())
    }


    const googleMapsHref = trackHistory?.pickup && trackHistory?.drop
        ? `https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=${encodeURIComponent(`${trackHistory.pickup.lat},${trackHistory.pickup.lng}`)}&destination=${encodeURIComponent(`${trackHistory.drop.lat},${trackHistory.drop.lng}`)}`
        : undefined

    return <div className='fixed top-14 left-1 right-1 bottom-1'>
        <div className='absolute right-5 top-5 z-10 flex flex-col gap-2'>
            <Link
                to="/orders/$orderId"
                params={{ orderId }}
                className='rounded-md bg-white/95 px-3 py-2 text-sm font-semibold text-slate-800 shadow-md hover:bg-white'
            >
                Back to order info
            </Link>
            {googleMapsHref ? (
                <Tooltip title="Opens driving directions in Google Maps">
                    <a
                        href={googleMapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className='rounded-md bg-white/95 px-3 py-2 text-sm font-semibold text-slate-800 shadow-md hover:bg-white'
                    >
                        Google maps
                    </a>
                </Tooltip>
            ) : (
                <span className='rounded-md bg-white/70 px-3 py-2 text-sm font-semibold text-slate-400 shadow-md'>
                    Google maps
                </span>
            )}
            {riderName || riderPhone ? (
                <div className='rounded-md bg-white/95 px-3 py-2 shadow-md'>
                    {lspId ? (
                        <div className='mb-2 flex items-center gap-2'>
                            {lspLogo ? <img src={lspLogo} className='h-6 w-6 rounded-full object-contain' alt={lspName || 'LSP'} /> : null}
                            <p className='text-xs font-semibold text-slate-700'>{lspName || lspId}</p>
                        </div>
                    ) : null}
                    <p className='text-xs text-slate-500'>Rider</p>
                    <p className='text-sm font-semibold text-slate-800'>{riderName || '-'}</p>
                    <div className='mt-1 flex items-center justify-between gap-3'>
                        <p className='text-xs text-slate-600'>{riderPhone || '-'}</p>
                        {riderPhone ? (
                            <a
                                href={`tel:${riderPhone}`}
                                className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-700 hover:bg-green-200'
                                aria-label="Call rider"
                            >
                                <Phone className='h-4 w-4' />
                            </a>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <button
                type="button"
                className='inline-flex items-center gap-2 rounded-md bg-white/95 px-3 py-2 text-sm font-semibold text-slate-800 shadow-md hover:bg-white disabled:cursor-not-allowed disabled:opacity-60'
                onClick={() => forceRefreshTrackLocation.mutate()}
                disabled={forceRefreshTrackLocation.isPending || getTrackHistory.isFetching || !getOrderInfo.data?.order?.orderId}
            >
                <RefreshCcw className={`h-4 w-4 ${forceRefreshTrackLocation.isPending ? 'animate-spin' : ''}`} />
                Refresh location
            </button>
        </div>
        {trackHistory && (trackHistory.location_history.length > 0 || liveRiderLocation) ? <><Map
            style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}
            defaultBounds={{
                north: Math.max(
                    ...trackHistory.location_history.map(e => parseFloat(e.lat)),
                    trackHistory.pickup.lat,
                    trackHistory.drop.lat,
                    liveRiderLocation?.lat || -90
                ),
                east: Math.max(
                    ...trackHistory.location_history.map(e => parseFloat(e.lng)),
                    trackHistory.pickup.lng,
                    trackHistory.drop.lng,
                    liveRiderLocation?.lng || -180
                ),
                south: Math.min(
                    ...trackHistory.location_history.map(e => parseFloat(e.lat)),
                    trackHistory.pickup.lat,
                    trackHistory.drop.lat,
                    liveRiderLocation?.lat || 90
                ),
                west: Math.min(
                    ...trackHistory.location_history.map(e => parseFloat(e.lng)),
                    trackHistory.pickup.lng,
                    trackHistory.drop.lng,
                    liveRiderLocation?.lng || 180
                ),
                padding: 250
            }}
            gestureHandling={'greedy'}
            disableDefaultUI={true}
            mapId={'DEMO_MAP_ID'}
            defaultZoom={15}
            defaultCenter={liveRiderLocation || (trackHistory.location_history.length === 1 ? { lat: parseFloat(trackHistory.location_history[0].lat), lng: parseFloat(trackHistory.location_history[0].lng) } : undefined)}
        >
            {trackHistory.location_history.map((e, i) => {
                const isRTO = trackHistory.rto_initiated_at && (dayjs(e.updated_at).isAfter(dayjs(trackHistory.rto_initiated_at)) || dayjs(e.updated_at).isSame(dayjs(trackHistory.rto_initiated_at)))
                let hoverColor: 'blue' | 'green' | 'orange' = 'blue'
                if (isRTO) {
                    hoverColor = 'orange'
                } else if (dayjs(e.updated_at).isAfter(dayjs(trackHistory.pickedup_at))) {
                    hoverColor = 'green'
                } else {
                    hoverColor = 'blue'
                }
                const markerFilter = hoverColor === 'orange'
                    ? 'hue-rotate(25deg) saturate(200%) brightness(110%)'
                    : hoverColor === 'green'
                        ? 'hue-rotate(95deg) saturate(150%)'
                        : 'hue-rotate(200deg) saturate(150%)'

                return <AdvancedMarker
                    position={{ lat: parseFloat(e.lat), lng: parseFloat(e.lng) }} onMouseEnter={() => setMarkerIndex(i)} key={i}>
                    <div className="flex flex-col items-center z-30" >
                        <p className={`${hoverColor === 'orange' ? 'bg-orange-200' : hoverColor === 'green' ? 'bg-green-200' : 'bg-blue-200'} text-black px-2 py-1 font-bold text-sm rounded-lg ${i !== highlightMarkerIndex ? 'hidden' : ''}`}>{dayjs(e.updated_at).format('MMM Do hh:mm:ss A')}</p>
                        <div className='flex justify-center items-center w-9 h-9 rounded-full bg-transparent' onMouseLeave={() => setMarkerIndex(-1)}>
                            <img
                                src={markerIcon}
                                className='w-6'
                                style={{ filter: markerFilter }}
                            />
                        </div>
                    </div>
                </AdvancedMarker>
            })}
            {trackHistory.pickup ? <AdvancedMarker position={trackHistory.pickup} draggable>
                <div className='bg-red-600 flex justify-center items-center w-9 h-9 rounded-full'>
                    <img src={storeIcon} className='w-6' />
                </div>
            </AdvancedMarker> : null}
            {trackHistory.drop ? <AdvancedMarker position={trackHistory.drop}>
                <div className='bg-green-600 flex justify-center items-center w-9 h-9 rounded-full'>
                    <img src={homeIcon} className='w-6' />
                </div>
            </AdvancedMarker> : null}
            {liveRiderLocation ? (
                <AdvancedMarker position={{ lat: liveRiderLocation.lat, lng: liveRiderLocation.lng }}>
                    <div className="flex flex-col items-center">
                        <p className='mb-1 rounded bg-black/80 px-2 py-1 text-xs font-semibold text-white'>
                            {`Current Rider Location at ${liveRiderLocation.updatedAt ? ` • ${dayjs(liveRiderLocation.updatedAt).format('hh:mm:ss A')}` : ''}`}
                        </p>
                        <div className='h-5 w-5 rounded-full border-2 border-white bg-rose-500 shadow-[0_0_0_8px_rgba(244,63,94,0.25)]' />
                    </div>
                </AdvancedMarker>
            ) : null}
        </Map>
            {showEventTimeline ? <div className='absolute left-5 w-64 top-5 bottom-5 overflow-auto rounded-lg'>
                <div className='bg-white border border-gray-200 px-3 py-5 overflow-auto w-full rounded-lg'>
                    <X className='absolute top-2 right-2 cursor-pointer w-5 h-5' onClick={() => setShowEventTimeline(false)} />
                    <EventTimeline events={getEventTimelines()} showDurations />
                </div>
            </div> : <div className='absolute left-5 top-5 rounded-full bg-black p-2 text-white'>
                <Tooltip title='Event Timeline'>
                    <Logs className='cursor-pointer' onClick={() => setShowEventTimeline(true)} />
                </Tooltip>
            </div>}
        </> : getTrackHistory.isLoading ? <p className='text-center text-2xl font-bold py-20'>Fetching track history...</p> : <p className='text-center text-2xl font-bold py-20'>No track history found</p>}
    </div >
}
